import { useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCatalogSearch } from "@/api/hooks";
import type { BiblioRecordSummary, RecordType } from "@/api/types";
import { BookCover } from "@/features/portal/components";
import { RECORD_TYPE_ICONS, RECORD_TYPE_LABELS } from "@/lib/recordTypes";

export function CatalogSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState(searchParams.get("q") ?? "");

  const query = searchParams.get("q") ?? "";
  const typeFilter = searchParams.get("type") as RecordType | null;

  const {
    data: records,
    isLoading,
    error,
  } = useCatalogSearch({
    q: query || undefined,
    type: typeFilter || undefined,
    limit: 50,
  });

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (inputValue.trim()) {
        setSearchParams({ q: inputValue.trim() });
      } else {
        setSearchParams({});
      }
    },
    [inputValue, setSearchParams]
  );

  const handleTypeFilter = useCallback(
    (type: RecordType | null) => {
      const params = new URLSearchParams(searchParams);
      if (type) {
        params.set("type", type);
      } else {
        params.delete("type");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Catalogue</h1>
        <div className="flex items-center gap-3">
          <Link
            to="/catalog/import"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
              />
            </svg>
            Importer
          </Link>
          <Link
            to="/catalog/new"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
          >
            <span>+</span>
            Nouvelle notice
          </Link>
        </div>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="relative flex-1">
          <input
            type="search"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            placeholder="Rechercher par titre, auteur, ISBN..."
            className="w-full rounded-lg border border-gray-300 px-4 py-3 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          <svg
            className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <button
          type="submit"
          className="rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          Rechercher
        </button>
      </form>

      {/* Type Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => {
            handleTypeFilter(null);
          }}
          className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
            !typeFilter
              ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
          }`}
        >
          Tous
        </button>
        {(["book", "periodical", "dvd", "cd", "ebook"] as RecordType[]).map((type) => (
          <button
            key={type}
            onClick={() => {
              handleTypeFilter(type);
            }}
            className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
              typeFilter === type
                ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            {RECORD_TYPE_ICONS[type]} {RECORD_TYPE_LABELS[type]}
          </button>
        ))}
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          Une erreur est survenue lors de la recherche.
        </div>
      ) : records && records.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {records.length} résultat{records.length > 1 ? "s" : ""}
          </p>
          <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
            {records.map((record) => (
              <RecordRow key={record.id} record={record} />
            ))}
          </div>
        </div>
      ) : query ? (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">Aucun résultat pour "{query}"</p>
        </div>
      ) : (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            Entrez un terme de recherche pour commencer
          </p>
        </div>
      )}
    </div>
  );
}

function RecordRow({ record }: { record: BiblioRecordSummary }) {
  return (
    <Link
      to={`/catalog/${record.id}`}
      className="block p-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
    >
      <div className="flex items-start gap-4">
        {/* Decorative: the title names the link and the document type is written out beside it. */}
        <div aria-hidden="true">
          <BookCover coverUrl={record.coverUrl} type={record.type} size="sm" />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">
            {RECORD_TYPE_LABELS[record.type]}
          </p>
          <h3 className="truncate font-medium text-gray-900 dark:text-white">{record.title}</h3>
          {record.subtitle && (
            <p className="truncate text-sm text-gray-500 dark:text-gray-400">{record.subtitle}</p>
          )}
          <div className="mt-1 flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            {record.authorsDisplay && <span>{record.authorsDisplay}</span>}
            {record.publisher && (
              <>
                <span className="text-gray-300 dark:text-gray-600">•</span>
                <span>{record.publisher}</span>
              </>
            )}
            {record.publicationYear && (
              <>
                <span className="text-gray-300 dark:text-gray-600">•</span>
                <span>{record.publicationYear}</span>
              </>
            )}
          </div>
          {record.isbn && (
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">ISBN {record.isbn}</p>
          )}
        </div>

        {/* Availability */}
        <div className="flex-shrink-0 text-right">
          <div
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
              record.availableItemsCount > 0
                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                : record.itemsCount > 0
                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                  : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
            }`}
          >
            {record.availableItemsCount > 0 ? (
              <>
                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                {record.availableItemsCount} disponible
                {record.availableItemsCount > 1 ? "s" : ""}
              </>
            ) : record.itemsCount > 0 ? (
              <>
                <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
                Emprunté
              </>
            ) : (
              "Pas d'exemplaire"
            )}
          </div>
          {record.itemsCount > 0 && (
            <p className="mt-1 text-xs text-gray-400">
              {record.itemsCount} exemplaire{record.itemsCount > 1 ? "s" : ""}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

export default CatalogSearchPage;
